import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function CurrencyMaster({ token }) {
  const emptyForm = {
    currency_code: '',
    currency_name: '',
    currency_symbol: '',
    is_active: true
  };

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const loadRows = async () => {
    try {
      const res = await fetch('/api/v1/core/currencies', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to load currencies');
        return;
      }
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading currencies');
    }
  };

  useEffect(() => {
    loadRows();
  }, [token]);

  const handleSave = async () => {
    const payload = {
      currency_code: (form.currency_code || '').trim(),
      currency_name: (form.currency_name || '').trim(),
      currency_symbol: (form.currency_symbol || '').trim(),
      is_active: form.is_active
    };
    if (!payload.currency_code) {
      setError('Currency code is required');
      return;
    }
    try {
      const res = await fetch(editingId ? `/api/v1/core/currencies/${editingId}` : '/api/v1/core/currencies', {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to save currency');
        return;
      }
      setError('');
      setForm(emptyForm);
      setEditingId(null);
      loadRows();
    } catch {
      setError('Network error while saving currency');
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      currency_code: row.currency_code || '',
      currency_name: row.currency_name || '',
      currency_symbol: row.currency_symbol || '',
      is_active: Boolean(row.is_active)
    });
    setError('');
    scrollToForm();
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`/api/v1/core/currencies/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to deactivate currency');
        return;
      }
      setError('');
      loadRows();
    } catch {
      setError('Network error while deleting currency');
    }
  };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Currency Master</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Currency' : 'Create Currency'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <input
            placeholder="Currency Code (e.g. INR)"
            value={form.currency_code}
            onChange={(e) => setForm({ ...form, currency_code: e.target.value.toUpperCase() })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <input
            placeholder="Currency Name"
            value={form.currency_name}
            onChange={(e) => setForm({ ...form, currency_name: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <input
            placeholder="Symbol (e.g. Rs.)"
            value={form.currency_symbol}
            onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })}
            style={{ padding: '0.75rem', borderRadius: '6px', border: '1px solid #dcdde1' }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
          <button onClick={handleSave} className="btn-primary" style={{ padding: '0.65rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            {editingId ? 'Update Currency' : 'Create Currency'}
          </button>
          {editingId && (
            <button onClick={() => { setEditingId(null); setForm(emptyForm); }} className="btn-muted" style={{ padding: '0.65rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3>Currencies</h3>
      {rows.length === 0 ? (
        <p>No currencies found</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Symbol</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Active</th>
              <th style={{ padding: '0.75rem', textAlign: 'left' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                <td style={{ padding: '0.75rem' }}>{row.currency_code}</td>
                <td style={{ padding: '0.75rem' }}>{row.currency_name || '-'}</td>
                <td style={{ padding: '0.75rem' }}>{row.currency_symbol || '-'}</td>
                <td style={{ padding: '0.75rem' }}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={{ padding: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  <button onClick={() => handleEdit(row)} style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.7rem', background: '#3498db', color: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(row.id)} className="btn-danger" style={{ border: 'none', borderRadius: '4px', padding: '0.45rem 0.7rem', cursor: 'pointer' }}>Deactivate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default CurrencyMaster;
